import { EditOutlined } from "@ant-design/icons";
import { Flex, Space, Tabs, Typography } from "antd";
import { VersionTag } from "../../../components/VersionTag.tsx";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { BreadcrumbTrail } from "../../../components/BreadcrumbTrail.tsx";
import { SavingProvider } from "../../../hooks/useSaving.tsx";
import { useTrailLabel } from "../../../hooks/useTrailLabel.ts";
import { routes } from "../../../routes.ts";
import { useRecipe } from "../RecipeContext.tsx";
import { CancelEditButton } from "./CancelEditButton.tsx";
import { DiscardDraftButton } from "./DiscardDraftButton.tsx";
import { SaveRecipeButton } from "./PublishRecipeButton.tsx";
import { RecipeGeneralTab } from "./RecipeGeneralTab.tsx";
import { RecipeIngredientsTab } from "./RecipeIngredientsTab.tsx";
import { RecipeLabelTab } from "./RecipeLabelTab.tsx";
import { RecipeNutritionTab } from "../view/RecipeNutritionTab.tsx";
import { RecipePhotosTab } from "./RecipePhotosTab.tsx";
import { RecipeUsedAsSubRecipeTab } from "../RecipeUsedAsSubRecipeTab.tsx";
import { SavingIndicator } from "../../../components/SavingIndicator.tsx";
import { RecipeTab } from "../constants.ts";
import { title } from "../../../styles/shared.styles.ts";

const recipeTabSchema = z.enum(RecipeTab).catch(RecipeTab.General);

export const RecipeDetailEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { recipe } = useRecipe();
  useTrailLabel(t("common.editName", { name: recipe.name }), EditOutlined);

  const activeTab = recipeTabSchema.parse(location.hash.replace("#", ""));

  const setActiveTab = (tab: string) => {
    void navigate({ hash: tab }, { replace: true });
  };

  const handleDiscardCompleted = () => {
    void navigate({ pathname: routes.recipeDetail.to(recipe.id), hash: location.hash });
  };

  return (
    <SavingProvider>
      <Flex gap="medium" vertical>
        <Flex gap="small" vertical>
          <BreadcrumbTrail />
          <Space wrap>
            <Typography.Title className={title} level={3}>
              {recipe.name}
            </Typography.Title>
            <VersionTag entity={recipe} />
          </Space>
          <Space wrap>
            <SaveRecipeButton />
            <CancelEditButton />
            <DiscardDraftButton onCompleted={handleDiscardCompleted} />
            <SavingIndicator />
          </Space>
        </Flex>

        <Tabs
          activeKey={activeTab}
          destroyOnHidden
          items={[
            {
              key: RecipeTab.General,
              label: t("recipes.tabGeneral"),
              children: <RecipeGeneralTab />,
            },
            {
              key: RecipeTab.Ingredients,
              label: t("recipes.tabIngredients", { count: recipe.ingredients.length }),
              children: <RecipeIngredientsTab />,
            },
            {
              key: RecipeTab.Nutrition,
              label: t("recipes.tabNutrition"),
              children: <RecipeNutritionTab />,
            },
            {
              key: RecipeTab.Label,
              label: t("recipes.tabLabel"),
              children: <RecipeLabelTab />,
            },
            {
              key: RecipeTab.Photos,
              label: t("recipes.tabPhotos", { count: recipe.photos.length }),
              children: <RecipePhotosTab />,
            },
            {
              key: RecipeTab.UsedAsSubRecipe,
              label: t("recipes.tabUsedAsSubRecipe", { count: recipe.usedAsSubRecipeIn.length }),
              children: <RecipeUsedAsSubRecipeTab />,
            },
          ]}
          onChange={setActiveTab}
        />
      </Flex>
    </SavingProvider>
  );
};
